import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatMoney, toAmount, usePagedRows } from "../../../../shared/components/reports";
import { Pagination } from "../../../../shared/ui/common/Pagination";
import { subjectName, type PractitionerReport, type ReportLinks } from "../../domain/PractitionerReport";
import MaybeLink from "./MaybeLink";

const HEADING = "py-2 pr-4 font-medium";
const CELL = "py-2.5 pr-4 text-content-primary";

/** `YYYY-MM-DDTHH:MM:SS` as the day and time it names, wherever the browser is. */
function wallClock(value: string, locale: string): string {
    const [day, time = ""] = value.split("T");
    const [year, month, date] = day.split("-").map(Number);
    const [hours = 0, minutes = 0] = time.split(":").map(Number);
    return new Date(year, month - 1, date, hours, minutes).toLocaleString(locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

/**
 * The sessions that are paid — the held ones — across the report's
 * professionals, oldest first: who held each, with whom, and what it is owed.
 */
export default function BillableSessionsTable({
    report,
    locale,
    links,
    only,
}: {
    report: PractitionerReport;
    locale: string;
    links: ReportLinks;
    /** One professional's sessions alone, when the page is filtered to them. */
    only?: string;
}) {
    const { t } = useTranslation();
    const rows = useMemo(
        () =>
            report.sessions.filter(
                (session) => session.status === "finished" && (!only || session.therapist_id === only)
            ),
        [report.sessions, only]
    );
    const paged = usePagedRows(rows);
    const groupOf = (count: number) => t("reports.groupOf", { count });
    const withCourse = report.training || rows.some((s) => s.course);
    const withModule = report.training || rows.some((s) => s.module);
    const total = rows.reduce((sum, s) => sum + (s.amount ? toAmount(s.amount) : 0), 0);

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <h2 className="text-base font-semibold text-content-primary">{t("reports.billable.title")}</h2>
                <p className="text-sm text-content-secondary">
                    {t("reports.billable.count", { count: rows.length })}
                    {report.has_amounts && ` · ${formatMoney(total, locale)}`}
                </p>
            </div>
            {rows.length === 0 ? (
                <p className="rounded-xl bg-surface-sunken p-6 text-center text-sm text-content-tertiary">
                    {t("reports.billable.empty")}
                </p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-content-tertiary dark:border-gray-800">
                                <th scope="col" className={HEADING}>{t("reports.sessions.date")}</th>
                                <th scope="col" className={HEADING}>{t("reports.mentors.name")}</th>
                                <th scope="col" className={HEADING}>{t("reports.table.subject")}</th>
                                {withCourse && <th scope="col" className={HEADING}>{t("reports.table.course")}</th>}
                                {withModule && <th scope="col" className={HEADING}>{t("reports.sessions.module")}</th>}
                                <th scope="col" className={HEADING}>{t("reports.table.type")}</th>
                                <th scope="col" className={`${HEADING} text-right`}>{t("reports.sessions.attendees")}</th>
                                {report.has_amounts && (
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.table.amount")}</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {paged.page.map((session) => (
                                <tr key={session.id} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                    <td className={`${CELL} whitespace-nowrap tabular-nums`}>
                                        <MaybeLink to={links.session(session)}>{wallClock(session.date, locale)}</MaybeLink>
                                    </td>
                                    <td className={CELL}>
                                        <MaybeLink to={session.therapist_id ? links.mentor(session.therapist_id) : null}>
                                            {session.therapist_name ?? "—"}
                                        </MaybeLink>
                                    </td>
                                    <th scope="row" className={`${CELL} font-medium`}>
                                        <MaybeLink to={links.subject(session.subject_type, session.subject_id)}>
                                            {subjectName(session, groupOf)}
                                        </MaybeLink>
                                    </th>
                                    {withCourse && <td className={CELL}>{session.course ?? "—"}</td>}
                                    {withModule && <td className={CELL}>{session.module ?? "—"}</td>}
                                    <td className={CELL}>{t(`reports.type.${session.type}`)}</td>
                                    <td className={`${CELL} text-right tabular-nums`}>
                                        {session.attendees !== null && session.participants
                                            ? `${session.attendees}/${session.participants}`
                                            : "—"}
                                    </td>
                                    {report.has_amounts && (
                                        <td className={`${CELL} text-right tabular-nums`}>
                                            {session.amount === null ? "—" : formatMoney(toAmount(session.amount), locale)}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <Pagination
                offset={paged.offset}
                total={paged.total}
                pageSize={paged.pageSize}
                onPrev={paged.onPrev}
                onNext={paged.onNext}
            />
        </section>
    );
}
